import { useMutation, useQuery } from "@tanstack/react-query";
import { get, post } from "../../api/client";
import {
  BulkStudentResultPayload,
  StudentResultPayload,
} from "../../Constants/Interfaces/payloadInterface/ResultsPayload.interface";
import queryClient from "../../config/queryClient";
import { useAppMutation } from "../customReactQuery";

export const Student_Result_Key = {
  STUDENT_RESULT_LISTS: "STUDENT_RESULT_LISTS",
};

export const useGetStudentListForResult = ({
  page = 1,
  resultId,
  onSuccess,
}: {
  page: number;
  resultId: number;
  onSuccess: (data: any) => unknown;
}) => {
  return useQuery(
    [Student_Result_Key.STUDENT_RESULT_LISTS, page, resultId],
    () => {
      if (!resultId) return;

      return get({
        url: `api/v1/results/students/${resultId}`,
        params: {
          page,
        },
      });
    },
    {
      onSuccess: (data: any) => {
        onSuccess(data);
      },
    },
  );
};

export const useSaveStudentResults = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (err: Error) => unknown;
}) =>
  useMutation(
    (body: StudentResultPayload) => {
      return post({
        url: "api/v1/results/save/studenrResult",
        body,
      });
    },
    {
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries({
          queryKey: [Student_Result_Key.STUDENT_RESULT_LISTS],
        });
      },
      onError: (err: Error) => onError(err),
    },
  );

/**
 * Same individual-student save endpoint as `useSaveStudentResults`, but
 * silent: no list refetch. Used by background autosave so an in-progress
 * edit on another row is not wiped by a reload.
 */
export const useSaveStudentResultInBackground = () =>
  useMutation((body: StudentResultPayload) => {
    return post({
      url: "api/v1/results/save/studenrResult",
      body,
    });
  });

export const useSaveAllStudentResults = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (err: Error) => unknown;
}) =>
  useMutation(
    (body: BulkStudentResultPayload) => {
      return post({
        url: "api/v1/results/save/bulkStudentResult",
        body,
      });
    },
    {
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries({
          queryKey: [Student_Result_Key.STUDENT_RESULT_LISTS],
        });
      },
      onError: (err: Error) => onError(err),
    },
  );

export const useDownloadStudentResult = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: any) => unknown;
  onError: (error: Error) => unknown;
}) =>
  useMutation(
    ({ resultId, studentId }: { resultId: number; studentId: number }) => {
      return post({
        url: "api/v1/results/generate-result",
        params: {
          resultId,
          studentId,
        },
      });
    },
    {
      onSuccess,
      onError: (error: Error) => onError(error),
    },
  );

export const useDownloadMarkLeader = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: any) => unknown;
  onError: (error: Error) => unknown;
}) => {
  return useAppMutation(
    ({ resultId }: { resultId: number }) => {
      return post({
        url: "api/v1/results/generate-mark-ledger",
        params: {
          resultId,
        },
      });
    },
    {
      onSuccess,
      onError: (error: Error) => onError(error),
    },
  );
};
